import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { trpcRetry } from "#/lib/trpc/retryPolicy"
import ResponseError from "../types/ResponseError"
import { Provider } from "#/pages/provider-finder/types/Providers"
import { trpc } from "#/lib/trpc/client"

const GET_CLINICS_BY_IDS_QUERY_KEY = "GetClinicsByIds"
const GET_DOULAS_BY_IDS_QUERY_KEY = "GetDoulasByIds"

// lat/long are distance-passthrough only; the BFF byIds routes hydrate by
// id and treat coords as optional. Keeping them optional here lets the
// recommend-doula redirect proceed when the eligible-doula record hasn't
// carried a location (invariant break) instead of dereffing undefined.
type ByIdsArgs = {
  providerUuids: number[]
  lat?: number
  long?: number
}

export function useClinicsByIds({ providerUuids, lat, long }: ByIdsArgs): UseQueryResult<Provider[], ResponseError> {
  return useQuery<Provider[], ResponseError>(
    [GET_CLINICS_BY_IDS_QUERY_KEY, providerUuids, lat, long],
    async () => {
      if (!providerUuids || providerUuids.length === 0) return []

      const result = await trpc.providerFinder.byIds.query({
        ids: providerUuids,
        lat: lat == null ? undefined : lat,
        lng: long == null ? undefined : long
      })
      return result.data
    },
    {
      refetchOnWindowFocus: false,
      retry: trpcRetry(1)
    }
  )
}

export function useDoulasByIds({ providerUuids, lat, long }: ByIdsArgs): UseQueryResult<Provider[], ResponseError> {
  return useQuery<Provider[], ResponseError>(
    [GET_DOULAS_BY_IDS_QUERY_KEY, providerUuids, lat, long],
    async () => {
      if (!providerUuids || providerUuids.length === 0) return []

      const result = await trpc.doulaRecommendation.byIds.query({
        ids: providerUuids,
        lat: lat == null ? undefined : lat,
        lng: long == null ? undefined : long
      })
      return result.data
    },
    {
      refetchOnWindowFocus: false,
      retry: trpcRetry(1)
    }
  )
}
